/**
 * Wave A · Mobile resilience pass.
 *
 * PageSkeleton — standard page-level loading placeholder for mobile.
 * Shows a header bar + list-item skeletons with shimmer animation.
 *
 * Uses the existing `shimmer` keyframe already defined in tailwind.config.ts.
 * bg-surface-2 tokens and border-line from DESIGN.md.
 */

import { cn } from '@/lib/cn';

interface ShimmerBarProps {
  className?: string;
}

function ShimmerBar({ className }: ShimmerBarProps) {
  return (
    <div
      aria-hidden
      className={cn(
        'relative overflow-hidden rounded-md bg-surface-3/70',
        className,
      )}
    >
      <div
        className="absolute inset-0 animate-shimmer"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, rgb(var(--surface-4) / 0.6) 50%, transparent 100%)',
          backgroundSize: '200% 100%',
        }}
      />
    </div>
  );
}

function ListItemSkeleton() {
  return (
    <div className="flex min-h-[64px] items-center gap-3 border-b border-line px-4 py-3">
      <ShimmerBar className="h-9 w-9 flex-shrink-0 rounded-full" />
      <div className="flex-1 space-y-2">
        <ShimmerBar className="h-3 w-3/4" />
        <ShimmerBar className="h-3 w-1/2" />
      </div>
    </div>
  );
}

interface PageSkeletonProps {
  /** Number of list-item skeletons to render (default: 5). */
  itemCount?: number;
  className?: string;
}

export function PageSkeleton({ itemCount = 5, className }: PageSkeletonProps) {
  return (
    <div
      role="status"
      aria-label="Carregando"
      aria-live="polite"
      className={cn('flex min-h-screen flex-col bg-surface', className)}
    >
      {/* Header skeleton */}
      <div className="flex h-[44px] items-center gap-3 border-b border-line bg-surface-2 px-4 pt-[env(safe-area-inset-top)]">
        <ShimmerBar className="h-6 w-6 rounded-full" />
        <ShimmerBar className="h-4 w-32" />
        <div className="flex-1" />
        <ShimmerBar className="h-6 w-6 rounded-full" />
      </div>

      {/* List-item skeletons */}
      <div className="flex-1">
        {Array.from({ length: itemCount }).map((_, i) => (
          <ListItemSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}
